import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Column } from '@app/partials/datatable/data-table/column';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { RouteParams } from '@app/shared/abstract/route-params';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { ReportModel, ReportStatus } from '../shared/report.model';
import { ReportService } from '../shared/report.service';
import { LucideAngularModule, FileText, Download, Trash, RefreshCw } from 'lucide-angular';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ToastService } from '@app/shared/toast/toast.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';

interface ReportTypeCard {
  type: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-reports-dashboard',
  standalone: true,
  imports: [
    DataTableComponent,
    LucideAngularModule,
    TextInputComponent,
    ButtonComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './reports-dashboard.component.html',
})
export class ReportsDashboardComponent implements OnInit {
  protected readonly ReportIcon = FileText;
  protected readonly DownloadIcon = Download;
  protected readonly TrashIcon = Trash;
  protected readonly RefreshIcon = RefreshCw;

  protected reportsPage?: PagedEntities<ReportModel>;
  protected searchControl = new FormControl('', { nonNullable: true });
  protected routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 25,
  });

  protected reportTypes: ReportTypeCard[] = [
    {
      type: 'financial_position',
      title: 'Financial Position',
      description: 'Revenue overview by category and extra items.',
    },
    {
      type: 'quote_vs_actual',
      title: 'Quote vs Actual',
      description: 'Compare linked venue quote values against confirmed locked event values.',
    },
    {
      type: 'drinks_packages',
      title: 'Drinks Packages',
      description: 'Drinks package selections and revenue by event.',
    },
    {
      type: 'product_report',
      title: 'Product Report',
      description: 'Confirmed products grouped by category, product, variant, and quantity.',
    },
    {
      type: 'cancellations',
      title: 'Cancellations',
      description: 'Cancelled events with cancellation dates.',
    },
    {
      type: 'pizza_offer',
      title: 'Pizza Offer',
      description: 'Pizza offer quantity, full price revenue and total discount.',
    },
  ];

  tableColumns: Column<ReportModel>[] = [
    {
      field: 'id',
      header: 'ID',
      type: 'number',
      sortable: true,
      align: 'right',
      callback: (report: ReportModel) => (report.id ?? '—').toString(),
    },
    {
      field: 'type',
      header: 'Report',
      sortable: true,
      callback: (report: ReportModel) => this.reportLabel(report.type),
    },
    {
      field: 'filters',
      header: 'Date Range',
      sortable: false,
      callback: (report: ReportModel) => this.formatDateRange(report),
    },
    {
      field: 'events_count',
      header: 'Events',
      type: 'number',
      sortable: true,
      align: 'right',
      callback: (report: ReportModel) => (report.events_count ?? 0).toString(),
    },
    {
      field: 'generated_at',
      header: 'Generated',
      sortable: true,
      callback: (report: ReportModel) => report.generated_at ? new Date(report.generated_at).toLocaleString() : '—',
    },
    {
      field: 'status',
      header: 'Status',
      type: 'badge',
      sortable: true,
      callback: (report: ReportModel) => this.statusLabel(report.status),
      getColor: (report: ReportModel) => this.statusColor(report.status),
    },
  ];

  rowActions = [
    {
      icon: this.DownloadIcon,
      callback: (report: ReportModel) => this.download(report),
      condition: (report: ReportModel) => report.status === 'completed',
      title: () => 'Download',
    },
    {
      icon: this.TrashIcon,
      callback: (report: ReportModel) => this.delete(report),
      title: () => 'Delete',
    },
  ];

  constructor(
    private reportService: ReportService,
    private toastService: ToastService,
    private router: Router,
    private confirmationModal: ConfirmationModalService,
  ) {}

  ngOnInit(): void {
    this.loadData();

    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((term) => {
        this.routeParams.filter = term ? [{ field: 'search', value: term }] : [];
        this.routeParams.pageNum = 1;
        this.loadData();
      });
  }

  get hasReports(): boolean {
    return (this.reportsPage?.data?.length ?? 0) > 0;
  }

  loadData(): void {
    this.reportService.getPage(this.routeParams).subscribe((res) => {
      this.reportsPage = res;
    });
  }

  handleRoute(params: RouteParams): void {
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.loadData();
  }

  refreshReports(): void {
    this.loadData();
  }

  createReport(type: string): void {
    this.router.navigate(['/reports/create', type]);
  }

  private download(report: ReportModel): void {
    if (!report.id) {
      return;
    }

    this.reportService.downloadReport(report.id).subscribe({
      next: (blob) => {
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = report.file_name || `${report.type}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        if (error?.status === 404) {
          this.toastService.error('Report not found. Refreshing list.');
          this.loadData();
          return;
        }
        if (error?.status === 422) {
          this.toastService.error('Report is not ready for download yet.');
          return;
        }
        this.toastService.error('Failed to download report.');
      },
    });
  }

  private async delete(report: ReportModel): Promise<void> {
    if (!report.id) {
      return;
    }

    const confirmDelete = await this.confirmationModal.confirm({
      title: 'Delete report',
      message: 'Delete this report? This cannot be undone.',
      confirmLabel: 'Delete Report',
      cancelLabel: 'Keep Report',
      confirmButtonStyle: 'danger',
    });

    if (!confirmDelete) {
      return;
    }

    this.reportService.deleteReport(report.id).subscribe({
      next: () => {
        this.toastService.success('Report deleted');
        this.loadData();
      },
      error: () => {
        this.toastService.error('Failed to delete report');
      },
    });
  }

  private reportLabel(type: string): string {
    if (type === 'extras_analysis') {
      return 'Product Report';
    }

    return this.reportTypes.find((report) => report.type === type)?.title ?? type;
  }

  private formatDateRange(report: ReportModel): string {
    const filters = report.filters;
    const dateFrom = filters?.date_from ? new Date(filters.date_from) : null;
    const dateTo = filters?.date_to ? new Date(filters.date_to) : null;

    if (!dateFrom && !dateTo) {
      return 'All dates';
    }

    if (dateFrom && dateTo) {
      return `${dateFrom.toLocaleDateString()} → ${dateTo.toLocaleDateString()}`;
    }

    if (dateFrom) {
      return `From ${dateFrom.toLocaleDateString()}`;
    }

    return `Until ${dateTo?.toLocaleDateString()}`;
  }

  private statusLabel(status: ReportStatus): string {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Ready';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  }

  private statusColor(status: ReportStatus): string {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'light';
    }
  }
}
